import { Route } from '@angular/router';

import {
  CanActivatePropertiesDetails,
  CanDeactivatePropertiesDetails,
  PropertyTabExistsGuard,
} from './properties.guards';
import { PropertiesResolver, PropertiesPropertyResolver, PropertyDeactivateGuard } from './properties.resolvers';
import { ProjectsFieldDefsResolver, ProjectDataResolver } from 'app/modules/projects/projects.resolvers';
import { SavedViewsResolver, SavedViewResolver, FeatureFlagGuard } from 'app/core';
import { PropertiesComponent } from './properties.component';
import { PropertiesListComponent } from './list/list.component';
import { PropertiesListTableComponent } from './list/table/table.component';
import { PropertiesListMapComponent } from './list/map/map.component';
import { PropertiesDetailsComponent } from './details/details.component';
import { RelatedToType } from 'app/shared/enums';
import { FeatureFlag } from '@core/enums';
import { RedirectToNewTaskModuleGuard } from '@tasks/guards/RedirectToNewTaskModule.guard';
import { AssetManagementContainerComponent } from '../asset-management/containers/asset-management-container/asset-management-container.component';
import { AssetManagementRouting } from '../asset-management/asset-management.routing';

export const propertiesRoutes: Route[] = [
  {
    path: '',
    component: PropertiesComponent,
    resolve: {
      options: ProjectDataResolver,
    },
    children: [
      {
        path: '',
        component: PropertiesListComponent,
        resolve: {
          fieldDefs: ProjectsFieldDefsResolver,
          options: ProjectDataResolver,
          savedViews: SavedViewsResolver,
        },
        children: [
          {
            path: ':saved_view_id/table',
            component: PropertiesListTableComponent,
            resolve: {
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
              savedViews: SavedViewsResolver,
              savedView: SavedViewResolver,
            },
          },
          {
            path: ':saved_view_id/map',
            component: PropertiesListMapComponent,
            resolve: {
              properties: PropertiesResolver,
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
              savedViews: SavedViewsResolver,
              savedView: SavedViewResolver,
            },
          },
        ],
      },
      {
        path: ':id',
        component: PropertiesDetailsComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [CanActivatePropertiesDetails],
        resolve: {
          property: PropertiesPropertyResolver,
          options: ProjectDataResolver,
          fieldDefs: ProjectsFieldDefsResolver,
        },
        canDeactivate: [CanDeactivatePropertiesDetails, PropertyDeactivateGuard],
        children: [
          {
            path: '',
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.PropertyDashboardModule),
            resolve: {
              fieldDefs: ProjectsFieldDefsResolver,
              options: ProjectDataResolver,
            },
          },
          {
            path: 'units',
            loadChildren: () => import('./details/tenants/tenants.module').then((m) => m.PropertyTenantsModule),
          },
          {
            path: 'leases',
            loadComponent: () => import('./details/leases/leases.component').then((m) => m.LeasesComponent),
          },
          {
            path: 'deals',
            loadChildren: () => import('./details/deals/deals.module').then((m) => m.PropertyDealsModule),
          },
          {
            path: 'nearby',
            loadChildren: () => import('./details/nearby/nearby.module').then((m) => m.PropertyNearbyModule),
          },
          {
            path: 'notes',
            loadChildren: () => import('../../shared/modules/notes/notes.module').then((m) => m.NotesModule),
          },
          {
            path: 'tasks',
            data: {
              relatedToType: RelatedToType.Property,
              taskModuleRelatedTo: RelatedToType.Property,
            },
            canActivate: [RedirectToNewTaskModuleGuard],
            loadChildren: () =>
              import('../../shared/modules/legacy-tasks/tasks.module').then((m) => m.LegacyTasksTabModule),
          },
          {
            path: 'new-tasks',
            loadChildren: () => import('@tasks/tasks.routes').then((m) => m.routes),
          },
          {
            path: 'folders',
            loadChildren: () => import('../../shared/modules/folders/folders.module').then((m) => m.FoldersTabModule),
          },
          {
            path: 'custom_object',
            data: {
              relatedTo: RelatedToType.Property,
            },
            loadChildren: () =>
              import('../../shared/modules/custom-objects/custom-objects.module').then((m) => m.CustomObjectsModule),
          },
          {
            path: AssetManagementRouting.assetManagement,
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: FeatureFlag.ASSET_MANAGEMENT, redirectPath: 'properties' },
            component: AssetManagementContainerComponent,
            children: AssetManagementRouting.routes,
          },
        ],
      },
      {
        path: ':id/details',
        component: PropertiesDetailsComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [CanActivatePropertiesDetails],
        resolve: {
          property: PropertiesPropertyResolver,
          options: ProjectDataResolver,
          fieldDefs: ProjectsFieldDefsResolver,
        },
        children: [
          {
            path: '',
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.PropertyDashboardModule),
          },
          {
            path: 'overview',
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.PropertyDashboardModule),
          },
          {
            path: 'tabs/:tab_id',
            loadChildren: () => import('./details/dashboard/dashboard.module').then((m) => m.PropertyDashboardModule),
            canActivate: [PropertyTabExistsGuard],
          },
        ],
      },
    ],
    title: 'Properties',
  },
];
